import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../../stores/useAuthStore';
import { legalService } from '../../../services/legalService';

/**
 * LGPD: quem está logado sem ter aceitado a versão vigente dos Termos/Política
 * — contas antigas, Google/GitHub ou versão nova — vê esta barra discreta no
 * rodapé, sem bloquear a tela, até clicar em "Li e aceito" (aceite ativo,
 * registrado na API com data, versão e IP). A autorização de uso das imagens
 * em portfólio/divulgação vem ao lado, opcional e desmarcada.
 */
const TermsAcceptanceBar: React.FC = () => {
  const { t } = useTranslation('auth');
  const user = useAuthStore((s) => s.user);
  const [marketingImages, setMarketingImages] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  const status = user?.legalTerms;
  if (!user || !status?.needsAcceptance) return null;

  const handleAccept = async () => {
    setSending(true);
    setError(false);
    try {
      const next = await legalService.accept(marketingImages);
      useAuthStore.setState({ user: { ...user, legalTerms: next } });
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  const link = 'text-snapfy-accent-soft underline underline-offset-2 hover:text-snapfy-text-primary';

  return (
    <div
      role="region"
      aria-label={t('legal.barLabel')}
      // No mobile fica acima da navegação inferior (fixed bottom-4, h-16) para não cobri-la.
      className="fixed inset-x-0 bottom-[84px] lg:bottom-0 z-[1300] flex justify-center px-3 pb-3 pointer-events-none"
    >
      <div className="pointer-events-auto w-full max-w-4xl flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-4 px-4 py-3 rounded-2xl border border-snapfy-border bg-snapfy-surface-hover/95 backdrop-blur-xl shadow-modal">
        <div className="flex flex-col gap-1.5 min-w-0 flex-1">
          <p className="m-0 text-xs leading-[1.5] text-snapfy-text-secondary">
            {status.acceptedVersion !== null ? t('legal.barUpdated') : t('legal.barText')}{' '}
            <a href="/terms" target="_blank" rel="noopener" className={link}>
              {t('legal.terms')}
            </a>{' '}
            {t('legal.and')}{' '}
            <a href="/privacy" target="_blank" rel="noopener" className={link}>
              {t('legal.privacy')}
            </a>
            .
          </p>
          <label className="flex items-start gap-2 text-[11px] leading-[1.45] text-snapfy-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={marketingImages}
              onChange={(e) => setMarketingImages(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-snapfy-border accent-[#7063f7]"
            />
            <span>{t('legal.marketingImages')}</span>
          </label>
          {error && <p className="m-0 text-[11px] text-snapfy-error">{t('legal.acceptFailed')}</p>}
        </div>
        <button
          type="button"
          onClick={() => void handleAccept()}
          disabled={sending}
          className="shrink-0 self-end sm:self-center h-9 px-4 rounded-xl bg-snapfy-gradient text-white text-xs font-semibold shadow-glow disabled:opacity-50"
        >
          {sending ? t('legal.sending') : t('legal.acceptButton')}
        </button>
      </div>
    </div>
  );
};

export default TermsAcceptanceBar;
